import React from 'react';
import { SpanStatusCode, trace } from '@opentelemetry/api';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

// Interface untuk props ErrorBoundary
export interface ErrorBoundaryProps {
  /** React children */
  children: React.ReactNode;
  /** Custom fallback component */
  fallback?: React.ComponentType<ErrorFallbackProps>;
  /** Custom error handler function */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Custom reset handler */
  onReset?: () => void;
  /** Apakah menampilkan reset button (default: true) */
  showResetButton?: boolean;
  /** Custom reset button text */
  resetButtonText?: string;
  /** Custom CSS class untuk fallback */
  fallbackClassName?: string;
}

const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({ 
  error, 
  resetErrorBoundary 
}) => {
  const serviceName = (window as any).REACT_APP_SIGNOZ_SERVICE_NAME || 'react-app';
  const tracer = trace.getTracer(serviceName);
  const span = tracer.startSpan(serviceName + ".error");

  React.useEffect(() => {
    span.setAttribute('error.message', error.message);
    span.setAttribute('error.stack', JSON.stringify(error.stack));
    span.setAttribute('error.name', error.name);

    span.recordException(error);
    span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });

    span.end();
  }, [error, span]);

  return (
    <div role="alert" className="error-boundary-fallback">
      <h1>Oops!</h1>
      <p>Terjadi kesalahan yang tidak terduga.</p>
      <p><i>{(error as any).statusText || error.message}</i></p>
      <button onClick={resetErrorBoundary}>Coba lagi</button>
    </div>
  );
};

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ 
  children,
  fallback: FallbackComponent,
  onError,
  onReset,
  showResetButton = true,
  resetButtonText = "Coba lagi",
  fallbackClassName = "",
}) => {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    // Tracing error
    const serviceName = (window as any).REACT_APP_SIGNOZ_SERVICE_NAME || 'react-app';
    const tracer = trace.getTracer(serviceName);
    const span = tracer.startSpan(serviceName + ".error");

    span.setAttribute('error.message', error.message);
    span.setAttribute('error.stack', JSON.stringify(error.stack));
    span.setAttribute('error.name', error.name);
    span.setAttribute('error.componentStack', errorInfo.componentStack || '');

    span.recordException(error);
    span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });

    span.end();

    // Call custom error handler
    if (onError) {
      onError(error, errorInfo);
    }
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    }
  };

  const CustomFallback: React.FC<ErrorFallbackProps> = (props) => {
    if (FallbackComponent) {
      return <FallbackComponent {...props} />;
    }

    return (
      <div className={fallbackClassName}>
        <DefaultErrorFallback {...props} />
      </div>
    );
  };

  return (
    <ReactErrorBoundary 
      FallbackComponent={CustomFallback}
      onError={handleError}
      onReset={handleReset}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary; 